// File: src/pages/FollowCostKPIEditor.tsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getAccessToken } from "../auth/getToken";
import { msalInstance } from "../auth/msalInstance";
import harnessBg from "../assets/images/harness-bg.png";
import ProjectCarousel from "../components/ProjectCarousel";
import TopMenu from "../components/TopMenu";

/* ----------------------------- Reusable UI ----------------------------- */
type DivProps = React.HTMLAttributes<HTMLDivElement>;
const Pill: React.FC<DivProps> = ({ className = "", children, ...rest }) => (
  <div
    className={[
      "rounded-full px-5 py-4",
      "backdrop-blur-xl backdrop-saturate-150",
      // lighter, less “dark banner”
      "bg-gradient-to-r from-white/30 via-white/20 to-white/10",
      "ring-1 ring-white/30",
      "shadow-[inset_0_1px_0_rgba(255,255,255,.65),0_6px_14px_rgba(0,0,0,.10)]",
      "hover:from-white/35 hover:via-white/25 hover:to-white/12 transition-colors",
      className,
    ].join(" ")}
    {...rest}
  >
    {children}
  </div>
);

const ActionButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    tone?: "blue" | "green" | "red" | "gray" | "neutral";
  }
> = ({ tone = "blue", className = "", children, ...props }) => {
  const tones: Record<string, string> = {
    blue: "bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-300",
    green: "bg-green-600 hover:bg-green-700 focus-visible:ring-green-300",
    red: "bg-red-600 hover:bg-red-700 focus-visible:ring-red-300",
    gray: "bg-slate-600 hover:bg-slate-700 focus-visible:ring-slate-300",
    neutral: "bg-white/20 hover:bg-white/30 focus-visible:ring-white/40",
  };
  return (
    <button
      className={[
        "rounded-full px-4 py-2 text-white font-medium",
        "shadow-md transition-colors",
        "focus-visible:outline-none focus-visible:ring-2",
        tones[tone],
        className,
      ].join(" ")}
      {...props}
    >
      {children}
    </button>
  );
};

interface BadgeProps {
  tone?: "green" | "yellow" | "gray";
  className?: string;
  children?: React.ReactNode;
}
const Badge: React.FC<BadgeProps> = ({
  tone = "gray",
  className = "",
  children,
}) => {
  const tones: Record<string, string> = {
    green: "bg-green-500/20 text-green-200 ring-1 ring-green-400/40",
    yellow: "bg-yellow-500/20 text-yellow-200 ring-1 ring-yellow-400/40",
    gray: "bg-white/20 text-white ring-1 ring-white/40",
  };
  return (
    <span
      className={[
        "inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold",
        tones[tone],
        className,
      ].join(" ")}
    >
      {children}
    </span>
  );
};
/* ---------------------------------------------------------------------- */

interface IFollowCostFields {
  Project: string;
  Area: string;
  Carline: string;
  InitiationReasons: string;
  BucketID: string;
  Date: string;
  Statut: string;
  Quantity: number;
  NettValue: number;
  TotalNettValue: number;
  Currency: string;
  BucketResponsible: string;
  PostnameID: string;
  Topic: string;
}

interface IFollowCostKPIItem {
  id: string;
  fields: IFollowCostFields;
}

interface IEditorRow {
  isEditing: boolean;
  draft: IFollowCostFields;
}

const LISTS_CONFIG_KEY = "cmConfigLists";

const FollowCostKPIEditor: React.FC = () => {
  const [siteId, setSiteId] = useState("");
  const [listId, setListId] = useState("");
  const [allItems, setAllItems] = useState<IFollowCostKPIItem[]>([]);
  const [items, setItems] = useState<IFollowCostKPIItem[]>([]);
  const [editorState, setEditorState] = useState<Record<string, IEditorRow>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<string>("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const navigate = useNavigate();

  // Load siteId & listId from localStorage
  useEffect(() => {
    const raw = localStorage.getItem(LISTS_CONFIG_KEY);
    if (!raw) return;
    try {
      const cfg = JSON.parse(raw);
      if (cfg?.siteId) setSiteId(cfg.siteId);

      // expect a `lists[]` entry named "FollowCostKPI"
      const follow = cfg?.lists?.find((l: any) => l?.name === "FollowCostKPI");
      if (follow?.listId) {
        setListId(follow.listId);
      } else {
        console.warn("FollowCostKPI listId not found in config", cfg?.lists);
      }
    } catch (e) {
      console.error("Failed to parse cmConfigLists:", e);
    }
  }, []);

  // Fetch items
  useEffect(() => {
    if (!siteId || !listId) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessToken(msalInstance, [
          "https://graph.microsoft.com/Sites.Manage.All",
        ]);
        const resp = await axios.get(
          `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listId}/items?expand=fields`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAllItems(resp.data?.value || []);
      } catch (err: any) {
        setError(err?.message || "Failed to load items.");
      } finally {
        setLoading(false);
      }
    })();
  }, [siteId, listId]);

  // Filter by project
  useEffect(() => {
    if (!selectedProject) setItems(allItems);
    else setItems(allItems.filter((it) => it.fields.Project === selectedProject));
    setCurrentPage(1);
  }, [selectedProject, allItems]);

  // Pagination
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedItems = items.slice(startIdx, startIdx + itemsPerPage);
  const pageCount = Math.ceil(items.length / itemsPerPage);

  // Editing handlers
  const handleRowDoubleClick = (item: IFollowCostKPIItem) => {
    setEditorState((prev) => ({
      ...prev,
      [item.id]: { isEditing: true, draft: { ...item.fields } },
    }));
  };
  const handleEditFieldChange = (
    itemId: string,
    field: keyof IFollowCostFields,
    value: string | number
  ) => {
    setEditorState((prev) => {
      const row = prev[itemId];
      if (!row) return prev;
      return {
        ...prev,
        [itemId]: { ...row, draft: { ...row.draft, [field]: value } },
      };
    });
  };
  const handleCancel = (itemId: string) => {
    setEditorState((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], isEditing: false },
    }));
  };
  const handleSave = async (itemId: string) => {
    const row = editorState[itemId];
    if (!row) return;

    const allowed: (keyof IFollowCostFields)[] = [
      "Project",
      "Area",
      "Carline",
      "InitiationReasons",
      "BucketID",
      "Date",
      "Statut",
      "Quantity",
      "NettValue",
      "TotalNettValue",
      "Currency",
      "BucketResponsible",
      "PostnameID",
      "Topic",
    ];

    const draft = { ...row.draft };
    if (draft.Date?.includes("T")) draft.Date = draft.Date.slice(0, 10);

    const fields = allowed.reduce((acc, k) => {
      (acc as any)[k] = draft[k];
      return acc;
    }, {} as Partial<IFollowCostFields>);

    try {
      const token = await getAccessToken(msalInstance, [
        "https://graph.microsoft.com/Sites.Manage.All",
      ]);
      await axios.patch(
        `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listId}/items/${itemId}/fields`,
        fields,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems((prev) =>
        prev.map((it) =>
          it.id === itemId ? { ...it, fields: { ...it.fields, ...fields } } : it
        )
      );
      handleCancel(itemId);
    } catch (err: any) {
      alert(
        "Error saving: " + (err?.response?.data?.error?.message || err?.message)
      );
    }
  };
  const handleDelete = async (itemId: string) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      const token = await getAccessToken(msalInstance, [
        "https://graph.microsoft.com/Sites.Manage.All",
      ]);
      await axios.delete(
        `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listId}/items/${itemId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems((prev) => prev.filter((it) => it.id !== itemId));
      setEditorState((prev) => {
        const nxt = { ...prev };
        delete nxt[itemId];
        return nxt;
      });
    } catch (err: any) {
      alert(
        "Error deleting: " +
          (err?.response?.data?.error?.message || err?.message)
      );
    }
  };

  const projectsForCarousel =
    JSON.parse(localStorage.getItem(LISTS_CONFIG_KEY) || "{}").projects || [];

  return (
    <div
      className="relative w-full min-h-screen bg-cover bg-center text-white"
      style={{ backgroundImage: `url(${harnessBg})` }}
    >
      {/* Top bar */}
      <div className="relative z-50 max-w-6xl mx-auto p-4 flex items-center gap-4">
        <TopMenu />
        <ActionButton tone="neutral" onClick={() => navigate(-1)}>
          ← Back
        </ActionButton>
      </div>

      {/* Page container */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 pb-10 space-y-6">
        {/* Header pill */}
        <Pill className="mt-2">
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-1xl sm:text-3xl lg:text-2xl font-extrabold tracking-tight drop-shadow-lg">
              Cost PA KPI Editor
            </h1>
            {selectedProject ? (
              <Badge tone="green" className="text-base">
                {selectedProject}
              </Badge>
            ) : (
              <Badge className="text-base">All projects</Badge>
            )}
          </div>
        </Pill>

        {/* Project filter carousel */}
        <div className="mt-2">
          <ProjectCarousel
            projects={projectsForCarousel}
            selectedProject={selectedProject}
            onProjectSelect={setSelectedProject}
          />
        </div>

        {/* Card container (lighter + glassy) */}
        <div className="mt-4 bg-white/8 border border-white/20 rounded-3xl shadow-xl backdrop-blur-md p-4 sm:p-6">
          {loading && (
            <div className="flex items-center gap-3 text-white/90">
              <div className="h-6 w-6 rounded-full border-4 border-white/30 border-t-white animate-spin" />
              Loading items…
            </div>
          )}
          {error && <p className="text-red-300">{error}</p>}
          {!loading && !error && items.length === 0 && <p>No items found.</p>}

          {paginatedItems.length > 0 && (
            <div className="overflow-x-auto">
              <div className="overflow-y-auto max-h-[60vh] rounded-2xl ring-1 ring-white/15">
                <table className="min-w-[1200px] w-full text-sm text-white">
                  <thead className="sticky top-0 z-20">
                    <tr className="bg-white/18 backdrop-blur-md backdrop-saturate-150 ring-1 ring-white/15">
                      {[
                        "Actions",
                        "Project",
                        "Area",
                        "Carline",
                        "InitiationReasons",
                        "BucketID",
                        "Date",
                        "Statut",
                        "Quantity",
                        "NettValue",
                        "TotalNettValue",
                        "Currency",
                        "BucketResponsible",
                        "PostnameID",
                        "Topic",
                      ].map((h, i, arr) => (
                        <th
                          key={h}
                          className={[
                            "p-3 text-left font-semibold tracking-wide",
                            i === 0 ? "first:rounded-tl-2xl" : "",
                            i === arr.length - 1 ? "last:rounded-tr-2xl" : "",
                          ].join(" ")}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {paginatedItems.map((itm) => {
                      const row = editorState[itm.id];
                      const editing = row?.isEditing;

                      const CellInput = ({
                        className = "w-28",
                        type = "text",
                        value,
                        onChange,
                      }: {
                        className?: string;
                        type?: string;
                        value: string | number;
                        onChange: React.ChangeEventHandler<HTMLInputElement>;
                      }) => (
                        <input
                          type={type}
                          className={[
                            "px-3 py-2 rounded-full",
                            "bg-white/85 text-black",
                            "outline-none focus:ring-2 focus:ring-blue-300",
                            "shadow-[inset_0_1px_2px_rgba(0,0,0,.12)]",
                            className,
                          ].join(" ")}
                          value={value}
                          onChange={onChange}
                          onClick={(e) => e.stopPropagation()}
                        />
                      );

                      return (
                        <tr
                          key={itm.id}
                          onDoubleClick={() => handleRowDoubleClick(itm)}
                          className="even:bg-white/6 hover:bg-white/12 transition-colors"
                        >
                          {/* Actions */}
                          <td className="p-3 whitespace-nowrap">
                            {editing ? (
                              <div className="flex items-center gap-2">
                                <ActionButton
                                  tone="green"
                                  className="px-3 py-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSave(itm.id);
                                  }}
                                >
                                  Save
                                </ActionButton>
                                <ActionButton
                                  tone="gray"
                                  className="px-3 py-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancel(itm.id);
                                  }}
                                >
                                  Cancel
                                </ActionButton>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <ActionButton
                                  tone="blue"
                                  className="px-3 py-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRowDoubleClick(itm);
                                  }}
                                >
                                  Edit
                                </ActionButton>
                                <ActionButton
                                  tone="red"
                                  className="px-3 py-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(itm.id);
                                  }}
                                >
                                  Delete
                                </ActionButton>
                              </div>
                            )}
                          </td>

                          {/* Project */}
                          <td className="px-3 py-2">
                            {editing ? (
                              <CellInput
                                className="w-32"
                                value={row?.draft.Project}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "Project",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              itm.fields.Project
                            )}
                          </td>

                          {/* Area */}
                          <td className="px-3 py-2">
                            {editing ? (
                              <CellInput
                                className="w-28"
                                value={row?.draft.Area}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "Area",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              itm.fields.Area
                            )}
                          </td>

                          {/* Carline */}
                          <td className="px-3 py-2">
                            {editing ? (
                              <CellInput
                                className="w-32"
                                value={row?.draft.Carline}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "Carline",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              itm.fields.Carline
                            )}
                          </td>

                          {/* InitiationReasons */}
                          <td className="px-3 py-2">
                            {editing ? (
                              <CellInput
                                className="w-60"
                                value={row?.draft.InitiationReasons}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "InitiationReasons",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              <span className="line-clamp-2">
                                {itm.fields.InitiationReasons}
                              </span>
                            )}
                          </td>

                          {/* BucketID */}
                          <td className="px-3 py-2">
                            {editing ? (
                              <CellInput
                                className="w-28"
                                value={row?.draft.BucketID}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "BucketID",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              itm.fields.BucketID
                            )}
                          </td>

                          {/* Date */}
                          <td className="px-3 py-2">
                            {editing ? (
                              <CellInput
                                type="date"
                                className="w-40"
                                value={row?.draft.Date}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "Date",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              itm.fields.Date
                            )}
                          </td>

                          {/* Statut */}
                          <td className="px-3 py-2">
                            {editing ? (
                              <CellInput
                                className="w-28"
                                value={row?.draft.Statut}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "Statut",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              <Badge
                                tone={
                                  /ok|done|close|closed|validated/i.test(
                                    itm.fields.Statut || ""
                                  )
                                    ? "green"
                                    : "yellow"
                                }
                              >
                                {itm.fields.Statut || "—"}
                              </Badge>
                            )}
                          </td>

                          {/* Quantity */}
                          <td className="px-3 py-2 text-right">
                            {editing ? (
                              <CellInput
                                type="number"
                                className="w-28 text-right"
                                value={row?.draft.Quantity}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "Quantity",
                                    Number(e.target.value)
                                  )
                                }
                              />
                            ) : (
                              itm.fields.Quantity
                            )}
                          </td>

                          {/* NettValue */}
                          <td className="px-3 py-2 text-right">
                            {editing ? (
                              <CellInput
                                type="number"
                                className="w-32 text-right"
                                value={row?.draft.NettValue}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "NettValue",
                                    Number(e.target.value)
                                  )
                                }
                              />
                            ) : (
                              itm.fields.NettValue
                            )}
                          </td>

                          {/* TotalNettValue */}
                          <td className="px-3 py-2 text-right">
                            {editing ? (
                              <CellInput
                                type="number"
                                className="w-36 text-right"
                                value={row?.draft.TotalNettValue}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "TotalNettValue",
                                    Number(e.target.value)
                                  )
                                }
                              />
                            ) : (
                              itm.fields.TotalNettValue
                            )}
                          </td>

                          {/* Currency */}
                          <td className="px-3 py-2">
                            {editing ? (
                              <CellInput
                                className="w-24"
                                value={row?.draft.Currency}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "Currency",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              itm.fields.Currency
                            )}
                          </td>

                          {/* BucketResponsible */}
                          <td className="px-3 py-2">
                            {editing ? (
                              <CellInput
                                className="w-40"
                                value={row?.draft.BucketResponsible}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "BucketResponsible",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              itm.fields.BucketResponsible
                            )}
                          </td>

                          {/* PostnameID */}
                          <td className="px-3 py-2">
                            {editing ? (
                              <CellInput
                                className="w-36"
                                value={row?.draft.PostnameID}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "PostnameID",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              itm.fields.PostnameID
                            )}
                          </td>

                          {/* Topic */}
                          <td className="px-3 py-2">
                            {editing ? (
                              <CellInput
                                className="w-60"
                                value={row?.draft.Topic}
                                onChange={(e) =>
                                  handleEditFieldChange(
                                    itm.id,
                                    "Topic",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              <span className="line-clamp-2">
                                {itm.fields.Topic}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex mt-6 gap-2 justify-center">
              <ActionButton
                tone="neutral"
                className="px-4 py-1.5 disabled:opacity-50"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                Prev
              </ActionButton>

              {Array.from({ length: pageCount }, (_, i) => i + 1).map((pg) => (
                <button
                  key={pg}
                  onClick={() => setCurrentPage(pg)}
                  className={[
                    "rounded-full px-4 py-1.5 text-sm font-semibold",
                    "transition-colors ring-1",
                    pg === currentPage
                      ? "bg-blue-600 ring-blue-400 text-white"
                      : "bg-white/20 ring-white/40 hover:bg-white/30",
                  ].join(" ")}
                >
                  {pg}
                </button>
              ))}

              <ActionButton
                tone="neutral"
                className="px-4 py-1.5 disabled:opacity-50"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, pageCount))}
                disabled={currentPage === pageCount}
              >
                Next
              </ActionButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowCostKPIEditor;
